import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TableHead, TableRow, TableCell, TableBody,
    TablePagination, TableSortLabel, Paper, Box,
    IconButton, Grid, Button, Link, Tooltip, Typography,
    Checkbox, FormControlLabel, useTheme
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { Direction, UserFields, UserListItemModel } from '../../typings'
import { GetLocalDateTimeString } from '../../DateUtilities';
import { usersFilter, paginationSelected, organizationSelected, markOne, markAll, markCurrentPage, clearSelection } from '../../app/usersSlice'
import { LoadingBackDrop, SelectInput, SkeletonTableRows, StyledTable, TitleBar, CheckInput, PrimarySaveConfirmationDialog } from '@lp/lp-ui';
import AddUserDialog from './AddUserDialog';
import BulkImportUsersDialog from './BulkImportUsersDialog';
import BulkImportDocDialog from './BulkImportDocDialog';
import BulkEnableDisableDialog from './BulkEnableDisableDialog';
import BulkMfaOnOffDialog from './BulkMfaOnOffDialog';
import BulkAssignRolesDialog from './BulkAssignRolesDialog';
import BulkEnableApplicationsDialog from './BulkEnableApplicationsDialog';
import BulkAddToOrganizationDialog from './BulkAddToOrganizationDialog';
import UserSearchFilters from './UserSearchFilters';
import InviteUserDialog from './InviteUserDialog';
import ResendInvitationDialog from './ResendInvitationDialog';
import InvitationChip from './InvitationChip';
import { userDataReset } from '../../app/userSlice';
import { useCurrentUser } from '../../app/useCurrentUser';
import identityService from '../../services/IdentityService';
import { operationStarted, subscribeToBulkProgress } from '../../app/bulkProgressSlice';

const EmptyCellText = ({ text }: { text: string }) => (
    <Typography component="span" color="textSecondary" fontStyle="italic">
        {text}
    </Typography>
);

export default function Users() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const currentUser = useAppSelector(store => store.currentUser);
    const { loading, data, pagination, organizationId, selectedItems, unSelectedItems, selectAll, selectAllCount } = useAppSelector(store => store.users);
    const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [resendTarget, setResendTarget] = useState<UserListItemModel | null>(null);
    // The API's own gate, so the UI never offers a route that answers 404.
    const inviteEnabled = currentUser.data?.inviteEnabled === true;
    const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
    const [bulkInviteDialogOpen, setBulkInviteDialogOpen] = useState(false);
    const [bulkImportDocOpen, setBulkImportDocOpen] = useState(false);
    const [isAdmin, isTenantAdmin, _] = useCurrentUser();

    // Bulk action dialog state
    const [enableDisableOpen, setEnableDisableOpen] = useState(false);
    const [mfaOnOffOpen, setMfaOnOffOpen] = useState(false);
    const [assignRolesOpen, setAssignRolesOpen] = useState(false);
    const [enableAppsOpen, setEnableAppsOpen] = useState(false);
    const [addToOrgOpen, setAddToOrgOpen] = useState(false);
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
    const [welcomeEmailOpen, setWelcomeEmailOpen] = useState(false);

    const [bulkLoading, setBulkLoading] = useState(false);

    const isRowChecked = (rowId: number): boolean => {
        if (selectAll) {
            return !unSelectedItems.includes(rowId);
        }
        return selectedItems.includes(rowId);
    };

    const selectedCount = selectAll ? selectAllCount - unSelectedItems.length : selectedItems.length;

    const currentPageIds = data.items.map(item => item.id);
    const checkedCurrentPageCount = currentPageIds.filter(id => isRowChecked(id)).length;
    const allCurrentPageChecked = currentPageIds.length > 0 && checkedCurrentPageCount === currentPageIds.length;
    const someCurrentPageChecked = checkedCurrentPageCount > 0;
    const allMatchingSelected = selectAll && unSelectedItems.length === 0 && data.totalCount > 0;

    const onHeaderCheckboxClick = () => {
        dispatch(markCurrentPage({ ids: currentPageIds, selected: !allCurrentPageChecked }));
    };

    const onSelectAllLinkClick = () => {
        if (allMatchingSelected) {
            dispatch(clearSelection());
        } else {
            dispatch(markAll(true));
        }
    };

    useEffect(() => {
        if (pagination.shouldApplySearch) {
            const pgn = {
                ...pagination,
                organizationId: organizationId
            };
            dispatch(paginationSelected(pgn));
            dispatch(usersFilter(pgn));
        }
    }, [pagination.shouldApplySearch]);

    useEffect(() => {
        const pgn = { ...pagination, page: { pageNumber: 1, pageSize: pagination.page.pageSize }, shouldApplySearch: true };
        dispatch(paginationSelected(pgn));
    }, [organizationId]);

    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        const pgn = { ...pagination, page: { pageNumber: newPage + 1, pageSize: pagination.page.pageSize }, shouldApplySearch: true };
        dispatch(paginationSelected(pgn));
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const pgn = { ...pagination, page: { pageNumber: 1, pageSize: parseInt(event.target.value, 10) }, shouldApplySearch: true };
        dispatch(paginationSelected(pgn));
    };

    const onOrganizationChanged = (e: React.ChangeEvent<any>) => {
        dispatch(organizationSelected(e.target.value));
    }

    const buildBulkRequest = () => ({
        isSelectAll: selectAll,
        selectedUserIds: selectedItems,
        unSelectedUserIds: unSelectedItems,
        filters: selectAll ? {
            sort: pagination.sort,
            filters: pagination.filters,
            page: pagination.page,
            filterString: pagination.filterString,
            loaded: pagination.loaded
        } : null,
        organizationId: organizationId
    });

    const refreshAfterBulkAction = () => {
        dispatch(clearSelection());
        dispatch(paginationSelected({ ...pagination, shouldApplySearch: true }));
    };

    // Not refreshAfterBulkAction: a resend is a single-row action and must not drop the admin's selection.
    const refreshUsers = () => {
        dispatch(paginationSelected({ ...pagination, shouldApplySearch: true }));
    };

    const trackBulkOperation = (response: { operationId: string; totalUsers: number; label: string }) => {
        dispatch(operationStarted({
            operationId: response.operationId,
            total: response.totalUsers,
            label: response.label
        }));
        dispatch(subscribeToBulkProgress(response.operationId));
    };

    // Bulk action handlers
    const handleEnableDisableSave = async (enable: boolean) => {
        setEnableDisableOpen(false);
        setBulkLoading(true);
        try {
            const response = await identityService.bulkEnableDisable({ ...buildBulkRequest(), enable });
            trackBulkOperation(response);
            refreshAfterBulkAction();
        } catch { /* handled by global error provider */ }
        finally { setBulkLoading(false); }
    };

    const handleMfaOnOffSave = async (enableMfa: boolean) => {
        setMfaOnOffOpen(false);
        setBulkLoading(true);
        try {
            const response = await identityService.bulkMfaOnOff({ ...buildBulkRequest(), enableMfa });
            trackBulkOperation(response);
            refreshAfterBulkAction();
        } catch { /* handled by global error provider */ }
        finally { setBulkLoading(false); }
    };

    const handleAssignRolesSave = async (roleIds: number[], enable: boolean) => {
        setAssignRolesOpen(false);
        setBulkLoading(true);
        try {
            const response = await identityService.bulkAssignRoles({ ...buildBulkRequest(), roleIds, enable });
            trackBulkOperation(response);
            refreshAfterBulkAction();
        } catch { /* handled by global error provider */ }
        finally { setBulkLoading(false); }
    };

    const handleEnableAppsSave = async (clientIds: number[], enable: boolean) => {
        setEnableAppsOpen(false);
        setBulkLoading(true);
        try {
            const response = await identityService.bulkEnableApplications({ ...buildBulkRequest(), clientIds, enable });
            trackBulkOperation(response);
            refreshAfterBulkAction();
        } catch { /* handled by global error provider */ }
        finally { setBulkLoading(false); }
    };

    const handleAddToOrgSave = async (targetOrganizationId: number, organizationRoleIds: number[]) => {
        setAddToOrgOpen(false);
        setBulkLoading(true);
        try {
            const response = await identityService.bulkAddToOrganization({ ...buildBulkRequest(), targetOrganizationId, organizationRoleIds });
            trackBulkOperation(response);
            refreshAfterBulkAction();
        } catch { /* handled by global error provider */ }
        finally { setBulkLoading(false); }
    };

    const handleResetPasswordConfirm = async () => {
        setResetPasswordOpen(false);
        setBulkLoading(true);
        try {
            const response = await identityService.bulkResetPassword(buildBulkRequest());
            trackBulkOperation(response);
            refreshAfterBulkAction();
        } catch { /* handled by global error provider */ }
        finally { setBulkLoading(false); }
    };

    const handleWelcomeEmailConfirm = async () => {
        setWelcomeEmailOpen(false);
        setBulkLoading(true);
        try {
            const response = await identityService.bulkSendWelcomeEmail(buildBulkRequest());
            trackBulkOperation(response);
            refreshAfterBulkAction();
        } catch { /* handled by global error provider */ }
        finally { setBulkLoading(false); }
    };

    const colSpan = (isAdmin ? 9 : 8) + (isTenantAdmin ? 1 : 0);

    const getField = (name: keyof UserListItemModel) =>
        UserFields.find(f => f.fieldName === name)!;

    const columnDefs: { fieldDef: ReturnType<typeof getField>; align: 'left' | 'center' }[] = [
        { fieldDef: getField('userName'), align: 'left' },
        { fieldDef: getField('fullName'), align: 'left' },
        { fieldDef: getField('email'), align: 'left' },
        { fieldDef: getField('phoneNumber'), align: 'left' },
        { fieldDef: getField('dateCreatedUtc'), align: 'left' },
        { fieldDef: getField('lastLoginDateUtc'), align: 'left' },
        { fieldDef: getField('enabled'), align: 'center' }
    ];

    return (
        <React.Fragment>
            <TitleBar
                title='Users'
                subtitle={`${data.totalCount} records found`}
                actionComponents={[
                    <React.Fragment key="sl-1">
                        {isAdmin &&
                            <Box display="flex" gap={1} alignItems="center">
                                <Button key="ac-1" variant="outlined" onClick={() => {
                                    dispatch(userDataReset());
                                    setNewUserDialogOpen(true);
                                }}>
                                    New User
                                </Button>
                                {inviteEnabled &&
                                    <Button key="ac-3" variant="outlined" startIcon={<ForwardToInboxOutlinedIcon />} onClick={() => {
                                        dispatch(userDataReset());
                                        setInviteDialogOpen(true);
                                    }}>
                                        Invite User
                                    </Button>
                                }
                                <Button key="ac-2" variant="outlined" startIcon={<FileUploadOutlinedIcon />} onClick={() => setBulkImportDialogOpen(true)}>
                                    Import Users
                                </Button>
                                {inviteEnabled &&
                                    <Button key="ac-4" variant="outlined" startIcon={<ForwardToInboxOutlinedIcon />} onClick={() => setBulkInviteDialogOpen(true)}>
                                        Invite Users
                                    </Button>
                                }
                                <Tooltip title="Bulk import documentation">
                                    <IconButton size="small" onClick={() => setBulkImportDocOpen(true)}>
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        }
                    </React.Fragment>
                ]}
            />
            <Paper>
                <Grid container spacing={1} p={1} alignItems="flex-start" justifyContent="space-between">
                    <Grid item xs={10}>
                        <UserSearchFilters />
                    </Grid>
                    <Grid item xs={2}>
                        {(() => {
                            const orgOptions = isTenantAdmin ? [
                                { value: 0, label: '-' },
                                ...(currentUser.data?.organizations ? currentUser.data.organizations.map(o => ({ value: o.id, label: o.displayName })) : [])
                            ] : (currentUser.data?.organizations ? currentUser.data.organizations.map(o => ({ value: o.id, label: o.displayName })) : []);
                            const rawValue = isTenantAdmin ? organizationId : currentUser.data?.organization.id;
                            const safeValue = orgOptions.some(o => o.value === rawValue) ? rawValue : (orgOptions[0]?.value ?? '');
                            return (
                                <SelectInput
                                    label="Organization"
                                    isEditing={isTenantAdmin}
                                    name='organizationSelector'
                                    options={orgOptions}
                                    onChange={onOrganizationChanged}
                                    onBlur={() => {}}
                                    helperText=""
                                    value={safeValue}
                                />
                            );
                        })()}
                    </Grid>
                </Grid>
                {isAdmin && selectedCount > 0 && (
                    <Grid
                        sx={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: theme.palette.background.paper,
                            zIndex: 2,
                            paddingTop: '10px',
                            paddingX: '28px',
                            borderBottom: `1px solid ${theme.palette.divider}`
                        }}
                        container
                        justifyContent="space-between"
                        alignItems="center"
                        minHeight="60px"
                    >
                        <Grid item>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="subtitle1" color="textSecondary">
                                    {selectedCount} USER(S) SELECTED
                                </Typography>
                                {data.totalCount > 0 && (
                                    <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        variant="subtitle1"
                                        onClick={onSelectAllLinkClick}
                                    >
                                        {allMatchingSelected ? 'Unselect All' : 'Select All'}
                                    </Link>
                                )}
                            </Box>
                        </Grid>
                        <Grid item>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button variant="outlined" size="small" onClick={() => setEnableDisableOpen(true)}>Enable/Disable</Button>
                                <Button variant="outlined" size="small" onClick={() => setMfaOnOffOpen(true)}>MFA On/Off</Button>
                                <Button variant="outlined" size="small" onClick={() => setResetPasswordOpen(true)}>Reset Password</Button>
                                <Button variant="outlined" size="small" onClick={() => setWelcomeEmailOpen(true)}>Send Welcome Email</Button>
                                <Button variant="outlined" size="small" onClick={() => setAssignRolesOpen(true)}>Roles</Button>
                                <Button variant="outlined" size="small" onClick={() => setEnableAppsOpen(true)}>Applications</Button>
                                {isTenantAdmin && <Button variant="outlined" size="small" onClick={() => setAddToOrgOpen(true)}>Add to Organization</Button>}
                            </Box>
                        </Grid>
                    </Grid>
                )}
                <Box sx={{ overflowX: 'auto', width: '100%' }}>
                    <StyledTable>
                        <TableHead>
                            <TableRow>
                                {isAdmin && (
                                    <TableCell>
                                        <FormControlLabel
                                            label=""
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    checked={allCurrentPageChecked}
                                                    indeterminate={someCurrentPageChecked && !allCurrentPageChecked}
                                                    disabled={currentPageIds.length === 0}
                                                    onChange={onHeaderCheckboxClick}
                                                />
                                            }
                                        />
                                    </TableCell>
                                )}
                                {
                                    columnDefs.map((obj) => (
                                        <TableCell
                                            key={obj.fieldDef.fieldName}
                                            align={obj.align}
                                        >
                                            <TableSortLabel
                                                active={pagination.sort.sortBy === (obj.fieldDef.fieldName)}
                                                direction={pagination.sort.sortDirection === Direction.Ascending
                                                    ? "asc"
                                                    : "desc"}
                                                onClick={() => {
                                                    dispatch(paginationSelected({
                                                        ...pagination,
                                                        sort: {
                                                            sortBy: obj.fieldDef.fieldName as keyof UserListItemModel,
                                                            sortDirection: pagination.sort.sortBy === obj.fieldDef.fieldName
                                                                ? (
                                                                    pagination.sort.sortDirection === Direction.Ascending ? Direction.Descending : Direction.Ascending
                                                                )
                                                                : pagination.sort.sortDirection
                                                        },
                                                        shouldApplySearch: true
                                                    }));
                                                }}
                                            >
                                                {obj.fieldDef.displayName}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                {isTenantAdmin && <TableCell key="organizations" align='left'>Organizations</TableCell>}
                                <TableCell key="next"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ?
                                <SkeletonTableRows rows={pagination.page.pageSize} columns={colSpan} />
                                :
                                data.items.map((row) => (
                                    <TableRow key={row.id}>
                                        {isAdmin && (
                                            <TableCell>
                                                <CheckInput
                                                    disabled={false}
                                                    label=""
                                                    isEditing={true}
                                                    value={isRowChecked(row.id)}
                                                    checked={isRowChecked(row.id)}
                                                    helperText=""
                                                    name=""
                                                    onChange={() => {
                                                        dispatch(markOne({ id: row.id, selected: !isRowChecked(row.id) }));
                                                    }}
                                                    onBlur={() => {}}
                                                    isPTNotRequired={true}
                                                    isSXRequired={true}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Link underline="hover" href={`/users/${row.id}`} onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(`/users/${row.id}`); }}>
                                                {row.userName}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{row.fullName?.trim() ? row.fullName : <EmptyCellText text="Not provided" />}</TableCell>
                                        <TableCell>{row.email ? row.email : <EmptyCellText text="No Email" />}</TableCell>
                                        <TableCell>{row.phoneNumber ? row.phoneNumber : <EmptyCellText text="No Phone #" />}</TableCell>
                                        <TableCell>{row.dateCreatedUtc ? GetLocalDateTimeString(new Date(row.dateCreatedUtc)) : ''}</TableCell>
                                        <TableCell>{row.lastLoginDateUtc ? GetLocalDateTimeString(new Date(row.lastLoginDateUtc)) : <EmptyCellText text="Never" />}</TableCell>
                                        <TableCell align='center'>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                {row.enabled ? <CheckIcon /> : <CloseIcon sx={{ color: 'text.secondary' }} />}
                                                <InvitationChip invited={row.invited} invitation={row.invitation} />
                                            </Box>
                                        </TableCell>
                                        {isTenantAdmin && <TableCell>{row.organizationsCsv ? row.organizationsCsv : <EmptyCellText text="No Organization" />}</TableCell>}
                                        <TableCell align="right">
                                            {inviteEnabled && isAdmin && row.invited &&
                                                <Tooltip title="Resend invitation">
                                                    <IconButton size="small" onClick={() => setResendTarget(row)}>
                                                        <ForwardToInboxOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                            <IconButton size="small" onClick={() => { navigate(`/users/${row.id}`) }}>
                                                <NavigateNextIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[10, 15, 20, 50, 100]}
                                    colSpan={colSpan}
                                    count={data.totalCount}
                                    rowsPerPage={pagination.page.pageSize}
                                    page={pagination.page.pageNumber - 1}
                                    SelectProps={{
                                        inputProps: {
                                            'aria-label': 'rows per page',
                                        },
                                        native: true
                                    }}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </TableRow>
                        </TableBody>
                    </StyledTable>
                </Box>
            </Paper>

            <AddUserDialog
                open={newUserDialogOpen}
                handleClose={() => { setNewUserDialogOpen(false); }}
            />

            <InviteUserDialog
                open={inviteDialogOpen}
                handleClose={() => { setInviteDialogOpen(false); }}
            />

            <ResendInvitationDialog
                user={resendTarget}
                onClose={() => setResendTarget(null)}
                onResent={refreshUsers}
            />

            <BulkImportUsersDialog
                open={bulkImportDialogOpen}
                handleClose={() => { setBulkImportDialogOpen(false); }}
            />

            <BulkImportUsersDialog
                mode="invite"
                open={bulkInviteDialogOpen}
                handleClose={() => { setBulkInviteDialogOpen(false); }}
            />

            <BulkImportDocDialog
                open={bulkImportDocOpen}
                handleClose={() => { setBulkImportDocOpen(false); }}
                inviteEnabled={inviteEnabled}
            />

            <BulkEnableDisableDialog
                open={enableDisableOpen}
                onClose={() => setEnableDisableOpen(false)}
                onSave={handleEnableDisableSave}
            />

            <BulkMfaOnOffDialog
                open={mfaOnOffOpen}
                onClose={() => setMfaOnOffOpen(false)}
                onSave={handleMfaOnOffSave}
            />

            <BulkAssignRolesDialog
                open={assignRolesOpen}
                onClose={() => setAssignRolesOpen(false)}
                onSave={handleAssignRolesSave}
            />

            <BulkEnableApplicationsDialog
                open={enableAppsOpen}
                onClose={() => setEnableAppsOpen(false)}
                onSave={handleEnableAppsSave}
            />

            <BulkAddToOrganizationDialog
                open={addToOrgOpen}
                onClose={() => setAddToOrgOpen(false)}
                onSave={handleAddToOrgSave}
            />
            <PrimarySaveConfirmationDialog
                title="Reset Password"
                contentText={`All ${selectedCount} selected user(s) will receive an email with a temporary password without any further confirmations. This action will be executed in the background and it may take some time for all changes to take effect. Do you want to proceed?`}
                handleSave={handleResetPasswordConfirm}
                handleClose={() => setResetPasswordOpen(false)}
                actionButtonText="Reset Password"
                open={resetPasswordOpen}
            />

            <PrimarySaveConfirmationDialog
                title="Send Welcome Email"
                contentText={`Are you sure you want to send a welcome email to the selected ${selectedCount} user(s)? This action will be executed in the background and it may take some time for all changes to take effect.`}
                handleSave={handleWelcomeEmailConfirm}
                handleClose={() => setWelcomeEmailOpen(false)}
                actionButtonText="Send"
                open={welcomeEmailOpen}
            />

            <LoadingBackDrop open={bulkLoading} />
        </React.Fragment>
    );
}
